import { Stack } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { ValueBadge } from '../styles/ValueBadge.styles';
import { memo } from 'react';
import { WeekValue } from '../../types';

interface WeekValueCellProps {
  value?: WeekValue | number | null | '';
  /**
   * Draws the badge neutral instead of colouring it by sign, and shows the number unsigned, for a row
   * that totals other rows. Pass a magnitude — how much there is to do — not a signed difference.
   */
  isSummary?: boolean;
  /**
   * What the badge reads. 'difference' subtracts the pair, which is the default this grid has always
   * drawn; 'ratio' shows the pair itself as `current/total`, for when the two counts matter more than
   * the gap between them. Either way the colour still comes from the difference, so a short week looks
   * short whichever way it is written.
   *
   * A value that is a plain number has no pair to show and is drawn as-is in both modes.
   */
  display?: 'difference' | 'ratio';
}

// Type guard
const isWeekValue = (value: unknown): value is WeekValue => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'current' in value &&
    'total' in value
  );
};

// Extract display value calculation
const getDisplayValue = (value: WeekValue | number): number => {
  return typeof value === 'number' ? value : value.current - value.total;
};

// Extract variant calculation
const getVariant = (value: number): 'negative' | 'zero' | 'positive' => {
  if (value < 0) return 'negative';
  if (value === 0) return 'zero';
  return 'positive';
};

// Format the display text
const formatDisplayValue = (value: number): string => {
  return value > 0 ? `+${value}` : `${value}`;
};

export const WeekValueCell = memo(
  ({ value, isSummary, display = 'difference' }: WeekValueCellProps) => {
    // Only an absent value renders nothing. A numeric 0 is a value — it says the week is met — and has
    // to reach the badge, which shows a checkmark for it.
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Early return for invalid values
    if (typeof value !== 'number' && !isWeekValue(value)) {
      return null;
    }

    const displayValue = getDisplayValue(value);
    const variant = isSummary ? 'neutral' : getVariant(displayValue);
    // Computed here so the type guard still narrows `value`. Null means "not a ratio", which covers
    // both the difference mode and a plain number, since a number carries no pair.
    const ratio =
      display === 'ratio' && isWeekValue(value)
        ? `${value.current}/${value.total}`
        : null;

    const content = () => {
      // A met week reads 3/3 rather than a checkmark: seeing the counts is the whole point of the mode.
      if (ratio) return ratio;
      // fontSize="inherit" sizes the tick from the badge's own caption fontSize in ValueBadge.styles.ts,
      // so there is no size to hard-code here.
      if (displayValue === 0) return <Icon type="check" fontSize="inherit" />;
      // A total of work to do, so no sign: abs also keeps it an amount if a caller hands over a signed
      // value.
      if (isSummary) return Math.abs(displayValue);
      return formatDisplayValue(displayValue);
    };

    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <ValueBadge variant={variant} data-variant={variant}>
          {content()}
        </ValueBadge>
      </Stack>
    );
  }
);
